/**
 * The grid's selection (docs/plan-builder-grid.md §1). Rows are the 7 days; `r = -1` is the
 * column header row. Columns are the draft's; `c = -1` is the day's location cell. A range only
 * ever covers data cells.
 */

export interface Pos {
  r: number;
  c: number;
}

export interface Selection {
  active: Pos;
  anchor: Pos;
  focus: Pos;
}

export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export const ROWS = 7;

export function at(r: number, c: number): Selection {
  const p = { r, c };
  return { active: p, anchor: p, focus: p };
}

export function isData(p: Pos): boolean {
  return p.r >= 0 && p.c >= 0;
}

export function rectOf(sel: Selection): Rect | null {
  if (!isData(sel.anchor) || !isData(sel.focus)) return null;
  return {
    top: Math.min(sel.anchor.r, sel.focus.r),
    bottom: Math.max(sel.anchor.r, sel.focus.r),
    left: Math.min(sel.anchor.c, sel.focus.c),
    right: Math.max(sel.anchor.c, sel.focus.c),
  };
}

export function rectSize(rect: Rect): { rows: number; cols: number } {
  return { rows: rect.bottom - rect.top + 1, cols: rect.right - rect.left + 1 };
}

export function inRect(rect: Rect | null, r: number, c: number): boolean {
  return !!rect && r >= rect.top && r <= rect.bottom && c >= rect.left && c <= rect.right;
}

export function isSingle(sel: Selection): boolean {
  return sel.anchor.r === sel.focus.r && sel.anchor.c === sel.focus.c;
}

export function cellsOf(rect: Rect): Pos[] {
  const out: Pos[] = [];
  for (let r = rect.top; r <= rect.bottom; r++) for (let c = rect.left; c <= rect.right; c++) out.push({ r, c });
  return out;
}

function valid(p: Pos, cols: number): boolean {
  if (p.r < -1 || p.r >= ROWS || p.c < -1 || p.c >= cols) return false;
  return !(p.r === -1 && p.c === -1);
}

function clamp(p: Pos, cols: number): Pos {
  const r = Math.max(-1, Math.min(ROWS - 1, p.r));
  const c = Math.max(-1, Math.min(cols - 1, p.c));
  if (r === -1 && c === -1) return { r: -1, c: 0 };
  return { r, c };
}

export function clampSelection(sel: Selection, cols: number): Selection {
  if (cols === 0) return at(0, -1);
  return { active: clamp(sel.active, cols), anchor: clamp(sel.anchor, cols), focus: clamp(sel.focus, cols) };
}

/** One step with the arrows; Shift extends the range from the anchor, data cells only. */
export function move(sel: Selection, dr: number, dc: number, cols: number, extend = false): Selection {
  if (extend && isData(sel.focus)) {
    const focus = { r: sel.focus.r + dr, c: sel.focus.c + dc };
    if (!isData(focus) || !valid(focus, cols)) return sel;
    return { ...sel, focus };
  }
  const next = { r: sel.active.r + dr, c: sel.active.c + dc };
  if (!valid(next, cols)) return at(sel.active.r, sel.active.c);
  return at(next.r, next.c);
}

/** Cmd+Arrow to the grid's edge; with Shift, the range grows to it. */
export function moveToEdge(sel: Selection, dr: number, dc: number, cols: number, extend = false): Selection {
  const from = extend ? sel.focus : sel.active;
  const minR = !extend && from.r === -1 ? -1 : 0;
  const target = {
    r: dr < 0 ? minR : dr > 0 ? ROWS - 1 : from.r,
    c: dc < 0 ? (from.c === -1 ? -1 : 0) : dc > 0 ? cols - 1 : from.c,
  };
  if (extend) {
    if (!isData(sel.anchor) || !isData(target)) return sel;
    return { ...sel, focus: target };
  }
  return at(target.r, target.c);
}

export function selectColumn(sel: Selection, c: number): Selection {
  const active = sel.active.c === c && sel.active.r >= 0 ? sel.active : { r: 0, c };
  return { active, anchor: { r: 0, c }, focus: { r: ROWS - 1, c } };
}

export function selectRow(sel: Selection, r: number, cols: number): Selection {
  if (cols === 0) return sel;
  const active = sel.active.r === r && sel.active.c >= 0 ? sel.active : { r, c: 0 };
  return { active, anchor: { r, c: 0 }, focus: { r, c: cols - 1 } };
}

export function selectAll(sel: Selection, cols: number): Selection {
  if (cols === 0) return sel;
  const active = isData(sel.active) ? sel.active : { r: 0, c: 0 };
  return { active, anchor: { r: 0, c: 0 }, focus: { r: ROWS - 1, c: cols - 1 } };
}

export function extendTo(sel: Selection, p: Pos): Selection {
  if (!isData(p) || !isData(sel.anchor)) return at(p.r, p.c);
  return { active: sel.anchor, anchor: sel.anchor, focus: p };
}

export function collapse(sel: Selection): Selection {
  return at(sel.active.r, sel.active.c);
}

export function selectRect(rect: Rect): Selection {
  return { active: { r: rect.top, c: rect.left }, anchor: { r: rect.top, c: rect.left }, focus: { r: rect.bottom, c: rect.right } };
}
